import {useCallback, useEffect, useState} from 'react';
import get from 'lodash/get';

import {getSurveyCreateRequestParams} from 'client/quicksurvey/survey-create/components/survey-creator/consts';

import {useCreateSurvey, useSurveys} from 'client/common/hooks/survey';
import {
    useAudienceCategories,
    useAudienceInterests,
    useAudienceSegments
} from 'client/common/hooks/audience';
import {useIsAuthorized, useUserCountryId} from 'client/common/hooks/user';
import {useCurrentOrg} from 'client/common/hooks/orgs';
import {
    SurveySlug,
    SurveyListItem,
    SurveyAudience,
    SurveyPreset,
    AudienceRequest,
    Resource,
    CreativeStat,
    BordomatikBox,
    CreativeBox
} from 'client/common/types';
import {SurveyState} from 'client/common/store/reducers/survey/types';

import {
    CreativeInfo,
    getAdditionalParameters,
    getCreativeInfo,
    getCreativeTargeting
} from 'client/creatives/utils/parse';
import {fetchCreativeDemoSurveys} from 'client/creatives/utils/demo';

import {CreativesBoxType, CreativeFields} from 'client/creatives/types';
import {StateFilterValue} from 'client/common/store/reducers/surveys/types';
import {SurveysFetchFormat} from 'client/common/utils/api-client/types';

import {useCreativeStat} from './stats';

export function useCreateCreative() {
    const createSurvey = useCreateSurvey();
    const isAuthorized = useIsAuthorized();
    const countryId = useUserCountryId();
    const orgId = useCurrentOrg();

    return useCallback(
        async (boxType: CreativesBoxType, clientId?: string) => {
            return createSurvey({
                survey: getSurveyCreateRequestParams({
                    preset: boxType as SurveyPreset,
                    clientId,
                    isAuthorized,
                    countryId,
                    orgId
                })
            });
        },
        [countryId, createSurvey, isAuthorized, orgId]
    );
}

export function useCreativeSurveys(
    surveyType: CreativesBoxType,
    stateFilter: StateFilterValue,
    clientId?: string,
    orgId?: number
) {
    const [demoSurveys, setDemoSurveys] = useState<SurveyListItem[]>([]);
    const [isDemoSurveysFetching, setIsDemoSurveysFetching] = useState(true);

    const {
        data: surveyLists,
        isFetching: isSurveysFetching,
        isFetchingNextPage: isFetchingMore,
        fetchNextPage: fetchMore,
        hasNextPage: canFetchMore
    } = useSurveys({
        filter: {
            state: stateFilter,
            surveyType: [surveyType],
            clientId,
            ...(orgId && {orgIds: orgId > 0 ? orgId : undefined})
        },
        format: SurveysFetchFormat.Creative,
        paginationKey: '0'
    });

    useEffect(() => {
        async function getDemoSurveys() {
            const surveys = await fetchCreativeDemoSurveys(surveyType);

            setDemoSurveys(surveys as any);
            setIsDemoSurveysFetching(false);
        }

        if (!isDemoSurveysFetching) {
            return;
        }

        void getDemoSurveys();
    }, [demoSurveys, setDemoSurveys, isDemoSurveysFetching, setIsDemoSurveysFetching, surveyType]);

    const ownSurveys: SurveyListItem[] =
        surveyLists && surveyLists.pages.length
            ? surveyLists.pages.reduce((prev: SurveyListItem[], curr) => {
                  if (curr && curr.surveys.length) {
                      prev.push(...curr.surveys);
                  }

                  return prev;
              }, [])
            : [];
    const surveys = ownSurveys.concat(demoSurveys);

    return {
        surveys,
        isFetching: isSurveysFetching || isDemoSurveysFetching,
        isFetchingMore,
        canFetchMore,
        fetchMore
    };
}

export interface UseCreativeParams {
    slug: SurveySlug;
    box?: CreativeBox | BordomatikBox;
    state?: SurveyState;
    audience?: SurveyAudience | AudienceRequest;
    resources?: Resource[];
}

export interface CreativeSurveyData extends CreativeFields {
    stat: CreativeStat;
    creative: CreativeInfo;
}
export function useCreative({
    slug,
    box,
    state,
    audience,
    resources = []
}: UseCreativeParams): Partial<CreativeSurveyData & {isFetching: boolean}> {
    const categories = useAudienceCategories();
    const interests = useAudienceInterests();
    const segments = useAudienceSegments();
    const stat = useCreativeStat(slug, state);

    const isFetched = [categories, interests, stat].every(({status}) => status === 'success');

    return {
        isFetching: !isFetched,
        stat: stat.data,
        ...(box && {
            creative: getCreativeInfo(box, resources),
            additionalParameters: getAdditionalParameters(box),
            brand: get(box, ['context', 'brand']),
            industry: get(box, ['context', 'industry'])
        }),
        targeting:
            audience && isFetched
                ? getCreativeTargeting({
                      audience,
                      categories: categories.data!,
                      interests: interests.data!,
                      segments: segments.data || []
                  })
                : ''
    };
}
